import {
  TableContainer,
  Table,
  Text,
  Thead,
  Tr,
  Th,
  Tbody,
  Tfoot,
  IconButton,
  Tag,
  TagLabel,
  Td,
  Input,
  Stack,
  Select,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaSortDown,
  FaSortUp,
} from "react-icons/fa";
import { HiChevronDown, HiChevronUp, HiPencil } from "react-icons/hi";
import { useAuth } from "../../hooks/useAuth";
import {
  FilterVariants,
  RequestData,
  Settlement,
  SettlementsFilter,
  SettlementStatus,
  STATUS_OPTIONS,
  TABLE_COLUMNS,
} from "../../utils/typings";
import { Show } from "../Show";
import { UpdateDeleteDrawer } from "../../components/UpdateDeleteDrawer";
import { useDisclosure } from "@chakra-ui/react";
import { CreateDrawer } from "../CreateDrawer";
import { getFormattedCurrency } from "../../utils/helpers";
import { CSVDownload } from "../CSVDownload";
import { Filter } from "../Filter";
import { VariantTypes } from "../../assets/images/Logo";

const getStatusColor = (status: SettlementStatus) => {
  switch (status) {
    case SettlementStatus.SETTLEMENT_COMPLETED:
      return "#00AE42";
    default:
      return "#FFB800";
  }
};

interface props {
  showFilters?: boolean;
  showEdit?: boolean;
  showCreate?: boolean;
  showCSV?: boolean;
  showSorting?: boolean;
  timeFilter?: SettlementsFilter;
  data: RequestData;
  totalRows: number;
  totalPages: number;
  fetchData: (page?: number, size?: number, params?: any) => void;
}

interface Filters {
  completedAt?: string;
  sendAmt?: string;
  sourceCcy?: string;
  receivedAmt?: string;
  targetFiatCcy?: string;
  status?: string;
}

export const DataTable = ({
  showFilters = false,
  showEdit = false,
  showCreate = false,
  showCSV = false,
  showSorting = false,
  timeFilter = SettlementsFilter.DAYS_7,
  data,
  totalRows,
  totalPages,
  fetchData,
  ...rest
}: props) => {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [pageIndex, setPageIndex] = useState(0);
  const [filteredData, setFilteredData] = useState<RequestData>(data);
  const [selectedSettlement, setSelectedSettlement] = useState<Settlement>();
  const [filterParams, setFilterParams] = useState<Filters>();

  const { user } = useAuth();
  const {
    onClose: onUpdateDeleteClose,
    isOpen: isUpdateDeleteOpen,
    onOpen: onUpdateDeleteOpen,
  } = useDisclosure();
  const {
    onClose: onCreateClose,
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
  } = useDisclosure();

  const dateFormat = "MMM DD, YYYY h:mma z";

  useEffect(() => {
    fetchData(pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    fetchData(pageIndex, 50, filterParams);
  }, [filterParams]);

  useEffect(() => {
    if (!!selectedSettlement) onUpdateDeleteOpen();
    else onUpdateDeleteClose();
  }, [selectedSettlement]);

  useEffect(() => {
    if (!!data) setFilteredData(data);
  }, [data]);

  const handleOnUpdateDeleteOpen = (settlement: Settlement) => {
    setSelectedSettlement(settlement);
  };

  const renderTableData = () => {
    const handleOnExpansionButtonClick = (index: number) => (event: any) => {
      if (expandedIndex === index) {
        setExpandedIndex(-1);
      } else {
        setExpandedIndex(index);
      }
    };

    const incomingSettlements = !!filteredData.settlements
      ? filteredData.settlements
      : data?.settlements;
    return incomingSettlements?.map((row: Settlement, index) => (
      <Tr borderBottom="1px solid rgba(0, 0, 0, 0.14)" key={index}>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          {row?.settlementId}
          <Show condition={expandedIndex === index}>
            <Text fontWeight={400} color="#54565A" textDecor="underline" pt={5}>
              Initiated At
            </Text>
            <Text fontWeight={400} color="#54565A">
              {moment(row?.initiatedAt).format(dateFormat)}
            </Text>
            <Show condition={user?.isAdmin}>
              <Text
                fontWeight={400}
                color="#54565A"
                textDecor="underline"
                pt={5}
              >
                Settlement Type
              </Text>
              <Text fontWeight={400} color="#54565A">
                {row?.settlementType}
              </Text>
            </Show>
          </Show>
        </Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          {moment(row?.completedAt).format(dateFormat)}
          <Show condition={expandedIndex === index}>
            <Text fontWeight={400} color="#54565A" textDecor="underline" pt={5}>
              Almond Fee
            </Text>
            <Text fontWeight={400} color="#54565A">
              {getFormattedCurrency(row?.almondFee, row?.sourceCcy)}
            </Text>
            <Show condition={user?.isAdmin}>
              <br />
              <Text fontWeight={400} color="#54565A" textDecor="underline">
                Source FI
              </Text>
              <Text fontWeight={400} color="#54565A">
                {row?.sourceFi || "-"}
              </Text>
            </Show>
          </Show>
        </Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          {row?.sendAmt}
          <Show condition={expandedIndex === index}>
            <Text fontWeight={400} color="#54565A" textDecor="underline" pt={5}>
              Transfer Crypto
            </Text>
            <Text fontWeight={400} color="#54565A">
              {row?.transferCryptoCcy}
            </Text>
            <Show condition={user?.isAdmin}>
              <Text
                fontWeight={400}
                color="#54565A"
                textDecor="underline"
                pt={5}
              >
                Target FI
              </Text>
              <Text fontWeight={400} color="#54565A">
                {row?.targetFi || <br />}
              </Text>
            </Show>
          </Show>
        </Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          {row?.sourceCcy}
          <Show condition={expandedIndex === index && user?.isAdmin}>
            <Text fontWeight={400} color="#54565A" textDecor="underline" pt={5}>
              Source Crypto Exchange
            </Text>
            <Text fontWeight={400} color="#54565A">
              {row?.sourceCryptoExchange || "-"}
            </Text>
          </Show>
        </Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          {row?.rate}
          <Show condition={expandedIndex === index && user?.isAdmin}>
            <Text fontWeight={400} color="#54565A" textDecor="underline" pt={5}>
              Target Crypto Exchange
            </Text>
            <Text fontWeight={400} color="#54565A">
              {row?.targetCryptoExchange}
            </Text>
          </Show>
        </Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">{row?.receivedAmt}</Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          {row.targetFiatCcy}
        </Td>
        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)">
          <Tag
            key={row?.status}
            borderRadius="full"
            variant="solid"
            bg={getStatusColor(row?.status)}
            p="5px 12px"
          >
            <TagLabel
              color={
                row?.status !== SettlementStatus.SETTLEMENT_COMPLETED
                  ? "Black"
                  : "White"
              }
            >
              {row?.status !== SettlementStatus.SETTLEMENT_COMPLETED
                ? "Pending"
                : "Completed"}
            </TagLabel>
          </Tag>
        </Td>

        <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)" textAlign="center">
          <IconButton
            variant="secondary"
            icon={
              expandedIndex === index ? (
                <HiChevronUp size="30px" />
              ) : (
                <HiChevronDown size="30px" />
              )
            }
            aria-label=""
            borderRadius="full"
            paddingStart="none"
            paddingEnd="none"
            onClick={handleOnExpansionButtonClick(index)}
          />
        </Td>
        <Show condition={showEdit}>
          <Td borderBottom="1px solid rgba(0, 0, 0, 0.14)" textAlign="center">
            <Show condition={user?.isAdmin}>
              <IconButton
                variant="secondary"
                icon={<HiPencil size="25px" color="black" />}
                aria-label=""
                borderRadius="full"
                bg="rgba(84, 86, 90, 0.1)"
                paddingStart="none"
                paddingEnd="none"
                onClick={() => handleOnUpdateDeleteOpen(row)}
              />
            </Show>
          </Td>
        </Show>
      </Tr>
    ));
  };

  const handleFilterChange = (event: any) => {
    const id: any = event.target.id;
    const incomingFilter = event.target.value;

    if (!incomingFilter) {
      const cleanedFilterParams: any = filterParams;
      delete cleanedFilterParams[id];
      setFilterParams(cleanedFilterParams);
    }

    setFilterParams({
      ...filterParams,
      [id]: incomingFilter,
    });
  };

  const handleSortChange = (id: string, direction: string) => () => {
    fetchData(data.page, data.size, {
      sortFields: [
        {
          sortField: id,
          sortDirection: direction,
        },
      ],
    });
  };

  const handleOnCreateClose = () => {
    fetchData(pageIndex, 10);
    onCreateClose();
  };

  const handleOnUpdateDeleteClose = () => {
    fetchData(pageIndex, data.size);
    onUpdateDeleteClose();
  };

  const handleNextPage = () => {
    const currentPage = pageIndex;
    setPageIndex(currentPage + 1);
  };

  const handlePreviousPage = () => {
    const currentPage = pageIndex;
    setPageIndex(currentPage - 1);
  };

  const renderHeaderColumn = (
    id: string,
    label: string,
    type: FilterVariants,
    options?: { hideSort: boolean }
  ) => (
    <Th
      borderBottom="1px solid rgba(0, 0, 0, 0.14)"
      justifyContent="space-between"
    >
      <Stack
        w="fit-content"
        h="fit-content"
        direction="row"
        alignItems="center"
      >
        <Text>{label}</Text>
        {/* Checks global and granualar tags to see if sorting is hidden */}
        {showSorting && !options?.hideSort && renderSortComponent(id)}
      </Stack>
      <Show condition={showFilters}>
        <br />
        <Filter
          variant={type}
          data={type === FilterVariants.Select ? STATUS_OPTIONS : id}
          onChange={handleFilterChange}
        />
      </Show>
    </Th>
  );

  const renderSortComponent = (id: string) => (
    <Stack w="fit-content" h="fit-content">
      <IconButton
        icon={<FaSortUp />}
        aria-label=""
        color=""
        px="0"
        m="0"
        w="fit-content"
        h="fit-content"
        onClick={handleSortChange(id, "ASC")}
      />{" "}
      <IconButton
        icon={<FaSortDown />}
        aria-label=""
        color=""
        px="0"
        mt="0 !important"
        w="fit-content"
        h="fit-content"
        onClick={handleSortChange(id, "DESC")}
      />
    </Stack>
  );

  return (
    <TableContainer
      border="1px solid rgba(0, 0, 0, 0.14)"
      shadow="md"
      borderRadius="12px"
      mt="16px"
    >
      <Table variant="simple">
        <Thead
          p="28px 30px"
          bg="linear-gradient(180deg, #FAFAFA 81.77%, #ECECEC 100%)"
        >
          <Tr>
            {renderHeaderColumn(
              "settlementId",
              TABLE_COLUMNS.settlementId.label,
              FilterVariants.Text,
              {
                hideSort: true,
              }
            )}
            {renderHeaderColumn(
              "completedAt",
              "Completed At",
              FilterVariants.Text
            )}
            {renderHeaderColumn("sendAmt", "Send Amount", FilterVariants.Text)}
            {renderHeaderColumn(
              "sourceCcy",
              "Source Currency",
              FilterVariants.Text
            )}
            {renderHeaderColumn("rate", "Rate", FilterVariants.Text)}
            {renderHeaderColumn(
              "receivedAmt",
              "Received Amount",
              FilterVariants.Text
            )}
            {renderHeaderColumn(
              "targetCcy",
              "Target Currency",
              FilterVariants.Text
            )}
            {renderHeaderColumn("status", "Status", FilterVariants.Select)}
            <Th borderBottom="1px solid rgba(0, 0, 0, 0.14)" textAlign="center">
              <Show condition={user?.isAdmin && showCreate}>
                <IconButton
                  variant="secondary"
                  icon={<FaPlus size="25px" />}
                  aria-label=""
                  borderRadius="full"
                  bg="#00AE42"
                  paddingStart="none"
                  paddingEnd="none"
                  onClick={onCreateOpen}
                />
              </Show>
            </Th>
            <Th borderBottom="1px solid rgba(0, 0, 0, 0.14)" textAlign="center">
              <Show condition={showCSV}>
                <CSVDownload data={data.settlements} />
              </Show>
            </Th>
          </Tr>
        </Thead>
        <Tbody>{renderTableData()}</Tbody>
        <Tfoot>
          <Tr>
            <Th>
              Showing{" "}
              {(filteredData?.page * filteredData?.size ||
                data?.page * data?.size) + 1}
              -
              {(filteredData?.page * filteredData?.size ||
                data?.page * data?.size) + filteredData?.settlements.length ||
                data?.settlements.length}{" "}
              for a total of {totalRows} records
            </Th>
            <Th></Th>
            <Th></Th>
            <Th></Th>
            <Th></Th>
            <Th></Th>
            <Th></Th>
            <Th></Th>
            <Show condition={user?.isAdmin && showEdit}>
              <Th></Th>
            </Show>
            <Th>
              <Stack direction="row" w="full" justifyContent="center">
                <IconButton
                  icon={<FaChevronLeft />}
                  aria-label="last page"
                  color="#212121"
                  disabled={pageIndex <= 0}
                  onClick={handlePreviousPage}
                />
                <Tag bg="#54565A" borderRadius="7px" color="white">
                  {pageIndex + 1}
                </Tag>
                <IconButton
                  icon={<FaChevronRight />}
                  aria-label="next page"
                  color="#212121"
                  disabled={pageIndex + 1 >= totalPages}
                  onClick={handleNextPage}
                />
              </Stack>
            </Th>
          </Tr>
        </Tfoot>
      </Table>
      <UpdateDeleteDrawer
        isOpen={isUpdateDeleteOpen}
        onClose={handleOnUpdateDeleteClose}
        onOpen={onUpdateDeleteOpen}
        data={selectedSettlement}
      />
      <CreateDrawer
        isOpen={isCreateOpen}
        onClose={handleOnCreateClose}
        onOpen={onCreateOpen}
      />
    </TableContainer>
  );
};
